import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ProgressBar } from '@/components/common/ProgressBar'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { programAssignmentsApi, type AssignedTheme, type ProgramAssignment } from '@/api/programs'
import { ThemeElementList } from '@/components/programs/ThemeElementList'
import { AssignmentStatisticsTab } from '@/components/programs/AssignmentStatisticsTab'
import { getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'

/**
 * Дорожная карта одной программы ученика.
 *
 * Будущие темы могут приходить скрытыми (hidden=true) — это политика видимости
 * назначения: у них есть позиция и статус, но нет содержимого. Позиция и общее
 * число тем остаются всегда, иначе прогресс «7 из 12» стал бы невычислим.
 */
export function MyProgramPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const assignmentId = Number(id)
  const { t } = useTranslation()

  // Вкладка в URL: после F5 ученик остаётся там же, где смотрел
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'results' ? 'results' : 'roadmap'

  const [assignment, setAssignment] = useState<ProgramAssignment | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setAssignment(await programAssignmentsApi.get(assignmentId))
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [assignmentId, t])

  useEffect(() => {
    if (Number.isFinite(assignmentId)) {
      void load()

      return
    }

    // /my/programs/abc: без этого страница навсегда оставалась в скелетоне
    setLoading(false)
    setError(t('common.error'))
  }, [assignmentId, load, t])

  const statusLabel = (theme: AssignedTheme): string => {
    if (theme.status === 'done') return t('programs.status.done', 'пройдена')
    if (theme.status === 'in_progress') return t('programs.status.inProgress', 'в работе')
    return t('programs.status.planned', 'впереди')
  }

  // Без этой ветки при упавшей загрузке рисовался пустой заголовок и
  // прогресс «0 из 0» — выглядело как программа без единой темы.
  if (loading || assignment === null) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Крошки известны до загрузки — под скелетон уходит только контент */}
          <PageBreadcrumbs items={[{ label: t('programs.my.title'), to: ROUTES.MY_PROGRAMS }]} />

          {!loading && <Alert severity="error">{error ?? t('common.error')}</Alert>}

          {loading && (
            <>
              <Skeleton variant="text" width="40%" height={44} />
              <Skeleton variant="rectangular" height={220} sx={{ mt: 2, borderRadius: 1 }} />
            </>
          )}
        </Box>
      </DashboardLayout>
    )
  }

  const themes = assignment?.themes ?? []
  const total = assignment?.group_progress?.themes_total ?? themes.length
  const done = themes.filter((theme) => theme.status === 'done').length
  const currentThemeId = assignment?.bookmark.theme_id ?? null

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs
          items={[{ label: t('programs.my.title'), to: ROUTES.MY_PROGRAMS }, { label: assignment?.name ?? '' }]}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <IconButton aria-label={t('common.back', 'Назад')} onClick={() => navigate(ROUTES.MY_PROGRAMS)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {assignment?.name}
          </Typography>
          <PageHelpPopover pageKey="my-programs" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          {t('programs.my.progress', '{{done}} из {{total}} тем', { done, total })}
        </Typography>
        <ProgressBar value={total > 0 ? (done / total) * 100 : 0} sx={{ mt: 1, mb: 2 }} />

        <Tabs value={tab} onChange={(_, value) => setSearchParams({ tab: value })} sx={{ mb: 2 }}>
          <Tab value="roadmap" label={t('programs.my.title')} />
          <Tab value="results" label={t('programs.stats.personalTitle')} />
        </Tabs>

        {/* Личный срез: сервер отдаёт ученику только его работы, без состава
            группы и без сравнения с одноклассниками */}
        {tab === 'results' && <AssignmentStatisticsTab assignmentId={assignmentId} />}

        {tab === 'roadmap' && themes.map((theme, index) => {
          const isCurrent = theme.id === currentThemeId
          // Скрытая политикой тема приходит болванкой без заголовка
          const isHidden = theme.hidden === true || theme.title === null

          return (
            <Card
              key={theme.id}
              sx={{ mb: 1.5, borderLeft: isCurrent ? 3 : 0, borderColor: 'primary.main' }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" fontWeight={isCurrent ? 700 : 500}>
                    {index + 1}.{' '}
                    {isHidden ? t('programs.my.hiddenTheme', 'Тема откроется позже') : theme.title}
                  </Typography>
                  <Chip size="small" label={statusLabel(theme)} />
                  {isCurrent && <Chip size="small" color="primary" label={t('programs.my.current', 'сейчас')} />}
                </Box>

                {!isHidden && theme.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {theme.summary}
                  </Typography>
                )}

                {!isHidden && (theme.elements?.length ?? 0) > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ThemeElementList elements={theme.elements ?? []} />
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </Box>
    </DashboardLayout>
  )
}
